import { useState, useEffect } from "react";
import { Checkbox, Label } from "flowbite-react";
import { HiExclamation } from "react-icons/hi";
import { useRebalancePreview } from "@repo/api";
import { useTranslation } from "@repo/core";
import type { PageProps } from "../types";

export const PreviewPage = ({
  strategy,
  selectedAccountId = "",
  onConfirmationChange,
}: PageProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { tStrategy } = useTranslation();

  const rebalancePreviewQuery = useRebalancePreview(
    selectedAccountId || "",
    strategy?.modelId || "",
    undefined,
    {
      enabled: !!selectedAccountId && !!strategy?.modelId,
    }
  );

  const rebalanceData = rebalancePreviewQuery.data;

  // Notify parent when confirmation changes
  useEffect(() => {
    onConfirmationChange?.(isConfirmed);
  }, [isConfirmed, onConfirmationChange]);

  // Reset confirmation when page loads
  useEffect(() => {
    setIsConfirmed(false);
  }, [selectedAccountId, strategy?.modelId]);

  const handleConfirmationChange = (checked: boolean) => {
    setIsConfirmed(checked);
  };

  if (rebalancePreviewQuery.isLoading) {
    return (
      <div className="text-center py-8">
        <span className="text-gray-500">{tStrategy("loadingRebalancePreview")}</span>
      </div>
    );
  }

  if (rebalancePreviewQuery.error) {
    return (
      <div className="text-center py-8">

        <div>
          Error loading rebalance preview:
        </div>
        <div>
          <span className="text-red-500">
            {String((rebalancePreviewQuery.error.data as { message: string }).message)}
          </span>
        </div>

      </div >
    );
  }

  if (!rebalanceData) {
    return (
      <div className="text-center py-8">
        <span className="text-gray-500">No rebalance data available</span>
      </div>
    );
  }

  // Process orders data
  const ordersArray = rebalanceData.orders
    ? Object.entries(rebalanceData.orders)
    : [];
  // Check if there are any actual orders (order !== 0)
  const hasValidOrders = ordersArray.some(([, order]) => order.order !== 0);

  return (
    <div className="space-y-6">

      {/* Confirmation Section or No Orders Info */}
      {hasValidOrders ? (
        <div>
          {/* Warning Banner */}
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <HiExclamation className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 font-medium">
              {tStrategy("tradesWillBeExecuted")}
            </p>
          </div>

          {/* Disclaimer Text */}
          <div className="space-y-3 text-sm text-gray-600 mb-6">
            <p>{tStrategy("aboutToSendInstructions")}</p>
            <p>
              {tStrategy("analyticalPlatformDisclaimer")}
            </p>
            <p>
              {tStrategy("tradingActivityDisclaimer")}
            </p>
          </div>

          {/* Confirmation Checkbox */}
          <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <Checkbox
              id="confirm-rebalance"
              checked={isConfirmed}
              onChange={(e) => handleConfirmationChange(e.target.checked)}
              className="mt-0.5"
            />
            <Label htmlFor="confirm-rebalance" className="text-gray-700 cursor-pointer">
              {tStrategy("confirmRebalanceCheckbox")}
            </Label>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-gray-500">
            {tStrategy("brokerageServicesFooter")}
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <HiExclamation className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-900 font-medium mb-1">
              {tStrategy("noRebalancingRequired")}
            </p>
            <p className="text-blue-800 text-sm">
              {tStrategy("portfolioOptimallyPositioned")}
            </p>
          </div>
        </div>
      )}

      {/* Rebalance Summary */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-3">
          {tStrategy("rebalancePreview")}
        </h4>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="font-semibold text-blue-800">
                {tStrategy("appliedCapital")}
              </span>{" "}
              <span className="text-blue-700">
                ${rebalanceData.capital?.toFixed(2) || "0.00"}
              </span>
            </div>
            <div>
              <span className="font-semibold text-blue-800">
                {tStrategy("calculatedLong")}
              </span>{" "}
              <span className="text-blue-700">
                ${rebalanceData.calculated_long?.toFixed(2) || "0.00"}
              </span>
            </div>
            <div>
              <span className="font-semibold text-blue-800">
                {tStrategy("calculatedShort")}
              </span>{" "}
              <span className="text-blue-700">
                ${rebalanceData.calculated_short?.toFixed(2) || "0.00"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {ordersArray.length > 0 && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">
            {tStrategy("rebalancingOrders")}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("ticker")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("order")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("position")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("price")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ordersArray.map(([ticker, order]) => (
                  <tr key={ticker} className="hover:bg-blue-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium">
                      {ticker}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {order.order}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {order.position}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      ${order.price?.toFixed(2) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Orders Data */}
      {ordersArray.length === 0 && (
        <div className="text-center py-8">
          <div className="bg-gray-50 p-6 rounded-lg">
            <span className="text-gray-600 text-lg">
              {tStrategy("noOrderDataAvailable")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
